/**
 * NOLAS 2026 — © Mikael (Mikaël FOURNY). Fork de la plateforme TNE 2026.
 * src/components/layout/DocumentBranding.tsx
 *
 * Rôle (projet « branding runtime multi-salon », 2026-07-06) :
 *   Pilote À CHAUD les éléments du <head> qui étaient FIGÉS au build dans
 *   `index.html` — donc impossibles à personnaliser sans rebuild :
 *     - `document.title`                       ← nom de l'événement ;
 *     - `<meta name="apple-mobile-web-app-title">` ← nom de l'événement ;
 *     - `<meta name="theme-color">`            ← couleur primaire du branding.
 *
 *   Source = l'événement actif (`useEventConfig` → `tne_events`, éditable au
 *   back-office). Un clone (nouveau salon) change son nom + sa couleur en
 *   base et ces éléments suivent SANS rebuild.
 *
 *   Zéro-régression : si l'événement n'a pas de couleur primaire dans son
 *   branding, on NE touche PAS `theme-color` (le hex figé d'index.html reste).
 *   Le titre suit toujours le nom de l'événement (défaut = « NOLAS 2026 »).
 *
 *   Composant d'effet (ne rend aucun DOM). Monté UNE fois au root de l'app
 *   (App.tsx), donc actif sur TOUTES les routes (y compris splash / login
 *   qui sont hors AppLayout).
 *
 * Voir : hooks/useEventConfig.ts, components/layout/EventThemeApplier.tsx
 *        (palette CSS), index.html (valeurs figées de repli),
 *        app/public_html/manifest.php (manifeste PWA dynamique).
 *
 * @author Mika
 * @since  2026-07-06
 */

import { useEffect } from 'react';
import { useEventConfig } from '@/hooks/useEventConfig';

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

/** Crée/met à jour une balise `<meta name>` du document. */
function setMetaContent(name: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

export function DocumentBranding(): null {
  const { event } = useEventConfig();
  const name = event.name;
  const primary = event.branding?.palette?.primary;

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (name && name.trim() !== '') {
      document.title = name;
      setMetaContent('apple-mobile-web-app-title', name);
    }
    if (typeof primary === 'string' && HEX_RE.test(primary)) {
      setMetaContent('theme-color', primary);
    }
  }, [name, primary]);

  return null;
}

export default DocumentBranding;
